import { useEffect, useRef, useState } from 'react';
import { Alert, Button, DatePicker, Segmented, Skeleton, Space, Typography } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useViewScope } from '../../utils/viewScope';
import { loadProtocolUsage, loadUnifiedProtocolUsage } from './api';
import { UnifiedProtocolUsageReport } from './UnifiedProtocolUsageReport';
import { unifiedProtocolScopeKey, type UnifiedProtocolUsage } from './unifiedProtocolUsage';
import { LatestScopeRequest, taipeiWindow } from './lib';
import { protocolScopeKey, validProtocolRange } from './protocolUsage';
import type { ProtocolSource, ProtocolUsage } from './protocolUsage';
import { ProtocolUsageReport } from './ProtocolUsageReport';
import './operations.css';
export { ProtocolUsageReport } from './ProtocolUsageReport';

const { Text, Title } = Typography;

type ProtocolUsageRangeProps = {
  range: { from: string; through: string };
  setRange: (range: { from: string; through: string }) => void;
};

export type ProtocolUsagePanelViewProps = ProtocolUsageRangeProps & {
  brand: string;
  loader?: typeof loadProtocolUsage;
};

/** The production entry point always obtains its brand from the authorized header view scope. */
export function ProtocolUsagePanel(props: ProtocolUsageRangeProps) {
  const { scope: brand } = useViewScope();
  return <UnifiedProtocolUsagePanelView {...props} brand={brand} />;
}

/** Explicit inputs allow mounting the real request lifecycle in isolated component tests. */
export function ProtocolUsagePanelView({ range, setRange, brand, loader = loadProtocolUsage }: ProtocolUsagePanelViewProps) {
  const [source, setSource] = useState<ProtocolSource>('native');
  const [refresh, setRefresh] = useState(0);
  const [dateError, setDateError] = useState(false);
  const [state, setState] = useState<{ key: string; data: ProtocolUsage | null; error: string | null; loading: boolean } | null>(null);
  const guard = useRef(new LatestScopeRequest());
  const key = protocolScopeKey(brand, source, range.from, range.through);
  const today = taipeiWindow(new Date(), 1).through;
  const earliest = taipeiWindow(new Date(), 30).from;
  const current = state?.key === key ? state : null;
  useEffect(() => {
    const controller = new AbortController();
    const request = guard.current.begin(key);
    setState(previous => ({ key, data: previous?.key === key ? previous.data : null, error: null, loading: true }));
    loader(brand, source, range.from, range.through, controller.signal).then(data => {
      if (controller.signal.aborted || !guard.current.isCurrent(request, key)) return;
      if (protocolScopeKey(data.brand, data.source, data.from, data.through) !== key) throw new Error('資料範圍與目前查詢不一致，請重試');
      setState({ key, data, error: null, loading: false });
    }).catch(error => {
      if (!controller.signal.aborted && guard.current.isCurrent(request, key)) setState({ key, data: null, error: error instanceof Error ? error.message : '讀取失敗', loading: false });
    });
    return () => { controller.abort(); guard.current.invalidate(); };
  }, [key, brand, source, range.from, range.through, refresh, loader]);
  const selectRecent = (days: number) => { setDateError(false); setRange(taipeiWindow(new Date(), days)); };
  return <section className="operations-page protocol-usage-panel" aria-label="協議使用">
    <div className="operations-header"><div><Title level={3} style={{ margin: 0 }}>協議使用</Title><Text type="secondary">依節點已回報用量，查看協議流量與使用會員</Text></div>
      <Space wrap><DatePicker.RangePicker aria-label="協議 BO 接收日期區間" value={[dayjs(range.from), dayjs(range.through)]} status={dateError ? 'error' : undefined} disabledDate={date => date.format('YYYY-MM-DD') < earliest || date.format('YYYY-MM-DD') > today} onChange={dates => {
        if (!dates?.[0] || !dates[1]) return;
        const from = dates[0].format('YYYY-MM-DD'); const through = dates[1].format('YYYY-MM-DD');
        const valid = validProtocolRange(from, through, earliest, today); setDateError(!valid); if (valid) setRange({ from, through });
      }} /><Button onClick={() => selectRecent(7)}>近 7 天</Button><Button onClick={() => selectRecent(30)}>近 30 天</Button><Button icon={<ReloadOutlined />} loading={current?.loading} onClick={() => setRefresh(value => value + 1)}>刷新</Button></Space>
    </div>
    <p>BO 接收日期 · 台北時間　{range.from} ～ {range.through}</p>
    {dateError && <Alert type="error" message="請選擇近 30 日內、最多 30 個曆日的有效日期區間" />}
    <Space wrap><Segmented aria-label="資料範圍" options={[{ label: '原生協議用量', value: 'native' }, { label: 'SK6 補充用量', value: 'ticket' }]} value={source} onChange={value => setSource(value as ProtocolSource)} /><Text type="secondary">兩個範圍分開統計，不能相加。</Text></Space>
    {current?.error && <Alert type="error" showIcon message="協議使用資料暫時無法讀取" description={current.error} action={<Button onClick={() => setRefresh(value => value + 1)}>重試</Button>} />}
    {!current || (current.loading && !current.data) ? <Skeleton active paragraph={{ rows: 8 }} /> : current.data && <><span role="status">{current.loading ? '刷新中，暫顯示上次結果' : ''}</span><ProtocolUsageReport key={key} data={current.data} /></>}
  </section>;
}

/** One canonical response is the normal view; old source reports remain diagnostic only. */
export function UnifiedProtocolUsagePanelView({ range, setRange, brand, loader = loadUnifiedProtocolUsage }: ProtocolUsageRangeProps & { brand: string; loader?: typeof loadUnifiedProtocolUsage }) {
  const [refresh, setRefresh] = useState(0);
  const [diagnostics, setDiagnostics] = useState(false);
  const [state, setState] = useState<{key: string; data: UnifiedProtocolUsage | null; error: string | null; loading: boolean} | null>(null);
  const guard = useRef(new LatestScopeRequest());
  const key = unifiedProtocolScopeKey(brand,range.from,range.through);
  const current = state?.key === key ? state : null;
  const today = taipeiWindow(new Date(),1).through; const earliest = taipeiWindow(new Date(),30).from;
  useEffect(() => {
    const controller = new AbortController(); const request = guard.current.begin(key);
    setState(previous => ({key,data:previous?.key === key ? previous.data : null,error:null,loading:true}));
    loader(brand,range.from,range.through,controller.signal).then(data => {
      if (controller.signal.aborted || !guard.current.isCurrent(request,key)) return;
      if (data.reportingMode !== 'canonical' || unifiedProtocolScopeKey(data.brand,data.from,data.through) !== key) throw new Error('資料範圍與目前查詢不一致，請重試');
      setState({key,data,error:null,loading:false});
    }).catch(error => {
      if (!controller.signal.aborted && guard.current.isCurrent(request,key)) setState({key,data:null,error:error instanceof Error ? error.message : '讀取失敗',loading:false});
    });
    return () => {controller.abort();guard.current.invalidate();};
  },[key,brand,range.from,range.through,refresh,loader]);
  return <section className="operations-page protocol-usage-panel" aria-label="統一協議使用">
    <div className="operations-header"><div><Title level={3}>協議使用</Title><Text type="secondary">統一計量，查看已回報流量與使用會員</Text></div><Space wrap>
      <DatePicker.RangePicker aria-label="協議 BO 接收日期區間" value={[dayjs(range.from),dayjs(range.through)]} disabledDate={date => date.format('YYYY-MM-DD') < earliest || date.format('YYYY-MM-DD') > today} onChange={dates => {
        if (!dates?.[0] || !dates[1]) return; const from = dates[0].format('YYYY-MM-DD'); const through = dates[1].format('YYYY-MM-DD');
        if (validProtocolRange(from,through,earliest,today)) setRange({from,through});
      }} />
      <Button onClick={() => setRange(taipeiWindow(new Date(),7))}>近 7 天</Button><Button onClick={() => setRange(taipeiWindow(new Date(),30))}>近 30 天</Button>
      <Button loading={current?.loading} onClick={() => setRefresh(value => value+1)}>刷新</Button>
    </Space></div><p>BO 接收日期 · 台北時間 {range.from} ～ {range.through}</p>
    {current?.error && <Alert type="error" message="協議使用資料暫時無法讀取" description={current.error} action={<Button onClick={() => setRefresh(value => value+1)}>重試</Button>} />}
    {!current || current.loading && !current.data ? <Skeleton active /> : current.data && <><span role="status">{current.loading ? '刷新中，暫顯示上次結果' : ''}</span><UnifiedProtocolUsageReport data={current.data} /></>}
    <details onToggle={event => setDiagnostics(event.currentTarget.open)}><summary>來源診斷</summary><p>供追查舊路徑使用。診斷來源與統一用量不能相加。</p>{diagnostics && <ProtocolUsagePanelView range={range} setRange={setRange} brand={brand} />}</details>
  </section>;
}
